/**
 * Builds the `sent` email entry the send-email modal appends to a form's
 * communication log. Client twin of `functions/main/src/local/createSentEmailLogEntry.ts`;
 * one UI renders both, so the emitted shape must stay in sync with it.
 * `body` is the clipped, stripped plain-text copy kept in the bundle for preview;
 * `bodyAttachment` links the formatted body as sent, already stored as an
 * attachment file, which the expanded row renders in full.
 */
import { Timestamp } from 'firebase/firestore';
import { ArtifactAttachments, EmailCommunicationRecord, SplitQuery, sanitizeEmailBody } from '#types';

interface CreateEmailLogEntryParams {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  message: string;
  userEmail?: string;
  includePdf: boolean;
  /** True only when an XLSX was actually attached to the outbound email (form may still have includeXlsx checked while no tables exist). */
  includeXlsxInEmail: boolean;
  selectedXlsxTableKeys?: string[];
  formAttachments: ArtifactAttachments;
  splitQuery?: SplitQuery;
  /** Filenames of binary attachments actually sent (PDF, XLSX); drives log count and labels */
  outboundAttachmentLabels?: string[];
  /** Same id used when uploading outbound files so versions link to this log entry */
  id?: string;
  /** Stored copies of outbound files (uploaded to form storage); enables download in the log */
  artifactAttachments?: ArtifactAttachments;
  /** The formatted body as sent, stored as an attachment file; absent when storing it failed */
  bodyAttachment?: EmailCommunicationRecord['bodyAttachment'];
}

/**
 * Creates an email communication log entry
 * @param params Parameters for creating the log entry
 * @returns EmailCommunicationRecord object
 */
export const createEmailLogEntry = ({
  to,
  cc,
  bcc,
  subject,
  message,
  userEmail,
  includePdf,
  includeXlsxInEmail,
  selectedXlsxTableKeys,
  formAttachments,
  splitQuery,
  outboundAttachmentLabels,
  id: idParam,
  artifactAttachments,
  bodyAttachment,
}: CreateEmailLogEntryParams): EmailCommunicationRecord => {
  const now = Timestamp.now();
  const entryId = idParam ?? crypto.randomUUID();

  const metadataNotes: string[] = [];

  if (splitQuery?.splitRef?.valuePath && splitQuery?.queryValue) {
    metadataNotes.push(`Split: ${splitQuery.splitRef.valuePath} = ${splitQuery.queryValue}`);
  }

  if (includePdf) {
    metadataNotes.push('PDF attached');
  }
  if (includeXlsxInEmail) {
    const keys = selectedXlsxTableKeys?.filter(Boolean) ?? [];
    if (keys.length > 0) {
      const preview = keys.slice(0, 3);
      const suffix = keys.length > preview.length ? ` +${keys.length - preview.length}` : '';
      metadataNotes.push(`XLSX attached (${preview.join(', ')}${suffix})`);
    } else {
      metadataNotes.push('XLSX attached');
    }
  }
  const attachmentFileCount = Object.keys(formAttachments).length;
  if (attachmentFileCount > 0) {
    metadataNotes.push(`${attachmentFileCount} file${attachmentFileCount === 1 ? '' : 's'} attached`);
  }

  const outboundLabels = (outboundAttachmentLabels ?? []).filter((name) => typeof name === 'string' && name.trim().length > 0);

  const storedAttachments: ArtifactAttachments =
    artifactAttachments && Object.keys(artifactAttachments).length > 0 ? artifactAttachments : {};
  const hasStoredAttachments = Object.keys(storedAttachments).length > 0;

  const metadataSuffix = metadataNotes.length > 0
    ? `\n\n[${metadataNotes.join('; ')}]`
    : '';

  const rawBodyWithMetadata = (message || '') + metadataSuffix;
  const { body: sanitizedBody } = sanitizeEmailBody(rawBodyWithMetadata);

  return {
    type: 'email',
    tag: 'sent',
    id: entryId,
    key: entryId,
    from: userEmail || '',
    to,
    cc,
    bcc,
    subject,
    body: sanitizedBody,
    bodyAttachment,
    timestamp: now,
    hasAttachments:
      hasStoredAttachments
      || metadataNotes.some((note) => note.includes('attached'))
      || (!hasStoredAttachments && outboundLabels.length > 0),
    attachments: hasStoredAttachments ? storedAttachments : {},
    outboundAttachmentLabels:
      !hasStoredAttachments && outboundLabels.length > 0 ? outboundLabels : undefined,
    createdAt: now,
    updatedAt: now,
  };
};
